import {
  CalendarDays,
  LayoutDashboard,
  Menu,
  Scissors,
  Settings,
  UserRound,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { cn, Dropdown } from '@agenda-facil/ui';
import { Brand } from '../components/brand';
import { ThemeToggle } from '../components/theme-toggle';
import { useAuthStore } from '../stores/auth-store';

const navigation = [
  { to: '/app', label: 'Início', icon: LayoutDashboard, end: true },
  { to: '/app/agenda', label: 'Agenda', icon: CalendarDays },
  { to: '/app/caixa', label: 'Caixa', icon: WalletCards },
  { to: '/app/clientes', label: 'Clientes', icon: UsersRound },
  { to: '/app/servicos', label: 'Serviços', icon: Scissors },
] as const;

export function AppLayout() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[var(--background)] md:grid md:grid-cols-[248px_1fr]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] border-r border-[var(--border)] bg-[var(--surface)] p-5 md:flex md:flex-col">
        <Brand className="px-2 py-3" />
        <nav className="mt-9 grid gap-1" aria-label="Menu do sistema">
          {navigation.map(({ to, label, icon: Icon, ...item }) => (
            <NavLink
              key={to}
              to={to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) =>
                cn(
                  'flex min-h-12 items-center gap-3 rounded-xl px-4 text-sm font-bold text-[var(--muted-foreground)] transition hover:bg-[var(--surface-raised)] hover:text-[var(--foreground)]',
                  isActive &&
                    'bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand)] hover:text-[var(--brand-foreground)]',
                )
              }
            >
              <Icon size={19} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto rounded-2xl bg-[var(--surface-raised)] p-4">
          <div className="flex items-center justify-between">
            <span className="grid size-9 place-items-center rounded-full bg-[var(--accent-soft)] text-sm font-black text-[var(--accent)]">
              {user?.name.slice(0, 1).toUpperCase()}
            </span>
            <ThemeToggle />
          </div>
          <p className="mt-3 truncate text-sm font-extrabold">{user?.name}</p>
          <p className="truncate text-xs text-[var(--muted-foreground)]">{user?.email}</p>
        </div>
      </aside>
      <div className="min-w-0 md:col-start-2">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-[var(--border)] bg-[color-mix(in_srgb,var(--background)_90%,transparent)] px-4 backdrop-blur md:hidden">
          <Brand compact />
          <Dropdown
            ariaLabel="Abrir menu da conta"
            align="end"
            trigger={
              <>
                <span className="max-w-28 truncate">{user?.name.split(' ')[0]}</span>
                <Menu size={20} aria-hidden="true" />
              </>
            }
            items={[
              {
                id: 'profile',
                label: 'Meu perfil',
                description: 'Dados pessoais e acesso',
                icon: <UserRound size={18} />,
                onSelect: () => void navigate('/app/perfil'),
              },
              {
                id: 'settings',
                label: 'Configurações',
                description: 'Preferências da barbearia',
                icon: <Settings size={18} />,
                onSelect: () => void navigate('/app/configuracoes'),
              },
            ]}
          />
        </header>
        <main className="mx-auto w-full max-w-[1280px] px-4 py-6 pb-28 md:px-8 md:py-9">
          <Outlet />
        </main>
      </div>
      <nav
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-[var(--border)] bg-[var(--surface)] px-2 pt-2 md:hidden"
        aria-label="Menu principal"
      >
        {navigation.map(({ to, label, icon: Icon, ...item }) => (
          <NavLink
            key={to}
            to={to}
            end={'end' in item ? item.end : false}
            className={({ isActive }) =>
              cn(
                'grid min-h-14 justify-items-center content-center gap-1 rounded-xl text-[10px] font-extrabold text-[var(--muted-foreground)]',
                isActive && 'bg-[var(--accent-soft)] text-[var(--accent)]',
              )
            }
          >
            <Icon size={20} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
